import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Search, Star, StarOff, Send, Sparkles, Newspaper, Trophy, TrendingUp, Radio,
    Loader2, MessageSquare, X, CalendarClock, Clock, Zap, Globe, Timer, Flag, Hash,
} from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
    article:        { label: "Article",     Icon: Newspaper,    tone: "text-blue-400  bg-blue-400/10" },
    external_news:  { label: "RSS",         Icon: Radio,        tone: "text-electric  bg-electric/10" },
    social_post:    { label: "Social",      Icon: Hash,         tone: "text-pink-300  bg-pink-500/10" },
    prediction:     { label: "Prediction",  Icon: TrendingUp,   tone: "text-emerald-300 bg-emerald-300/10" },
    match:          { label: "Match",       Icon: Trophy,       tone: "text-yellow-300 bg-yellow-300/10" },
    live_match:     { label: "LIVE",        Icon: Zap,          tone: "text-red-400   bg-red-400/10" },
    fixture:        { label: "Fixture",     Icon: Timer,        tone: "text-cyan-300  bg-cyan-300/10" },
    result:         { label: "Result",      Icon: Flag,         tone: "text-orange-300 bg-orange-300/10" },
    web_article:    { label: "Web",         Icon: Globe,        tone: "text-purple-300 bg-purple-300/10" },
};

const KIND_FILTERS = [
    { id: "all", label: "All" },
    { id: "live_match", label: "Live" },
    { id: "fixture", label: "Fixtures" },
    { id: "result", label: "Results" },
    { id: "social_post", label: "Social" },
    { id: "article", label: "Articles" },
    { id: "external_news", label: "RSS" },
    { id: "prediction", label: "Predictions" },
    { id: "web_article", label: "Web" },
];

const ContentHubSearch = ({ onChanged }) => {
    const { token, user } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
    const isAdmin = user?.role === "admin";

    const [q, setQ] = useState("");
    const [debounced, setDebounced] = useState("");
    const [activeKind, setActiveKind] = useState("all");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const [smsItem, setSmsItem] = useState(null);
    const [smsBases, setSmsBases] = useState([]);
    const [smsBaseId, setSmsBaseId] = useState("");
    const [smsBusy, setSmsBusy] = useState(false);
    const [smsResult, setSmsResult] = useState(null);

    // Scheduled feature dialog
    const [featureItem, setFeatureItem] = useState(null);
    const [featureFrom, setFeatureFrom] = useState("");
    const [featureUntil, setFeatureUntil] = useState("");
    const [featureBusy, setFeatureBusy] = useState(false);

    // Debounce query
    useEffect(() => {
        const t = setTimeout(() => setDebounced(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        if (debounced.length < 2) {
            setResults([]);
            return;
        }
        const run = async () => {
            setLoading(true);
            try {
                const params = { q: debounced, limit: 24 };
                if (activeKind !== "all") params.types = activeKind;
                const r = await axios.get(`${API}/admin/content-hub/search`, { headers: authH, params });
                setResults(r.data?.results || []);
            } catch (e) {
                toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [debounced, activeKind, authH]);

    const toggleFeature = async (row) => {
        if (row.is_featured) {
            // Direct unfeature
            setBusyId(row.id);
            try {
                await axios.post(
                    `${API}/admin/content-hub/unfeature`,
                    { kind: row.kind, item_id: row.id },
                    { headers: authH },
                );
                setResults((rs) => rs.map((r) => (r.id === row.id ? { ...r, is_featured: false } : r)));
                toast.success("Removed from homepage");
                onChanged?.();
            } catch (e) {
                toast.error("Unfeature failed", { description: e?.response?.data?.detail || e.message });
            } finally {
                setBusyId(null);
            }
            return;
        }
        // Open schedule dialog
        setFeatureItem(row);
        setFeatureFrom("");
        setFeatureUntil("");
    };

    const _toIsoOrNull = (local) => {
        if (!local) return null;
        try { return new Date(local).toISOString(); } catch { return null; }
    };

    const applyFeature = async (preset) => {
        if (!featureItem) return;
        setFeatureBusy(true);
        const now = new Date();
        let from = null;
        let until = null;
        if (preset === "forever") {
            // both null
        } else if (preset === "24h") {
            until = new Date(now.getTime() + 24 * 3600 * 1000).toISOString();
        } else if (preset === "48h") {
            until = new Date(now.getTime() + 48 * 3600 * 1000).toISOString();
        } else if (preset === "week") {
            until = new Date(now.getTime() + 7 * 24 * 3600 * 1000).toISOString();
        } else if (preset === "custom") {
            from = _toIsoOrNull(featureFrom);
            until = _toIsoOrNull(featureUntil);
            if (!from && !until) {
                toast.error("Pick at least one date — or use a preset");
                setFeatureBusy(false);
                return;
            }
        }
        try {
            const r = await axios.post(
                `${API}/admin/content-hub/feature`,
                {
                    kind: featureItem.kind,
                    item_id: featureItem.id,
                    title: featureItem.title,
                    summary: featureItem.summary,
                    image_url: featureItem.image_url,
                    feature_from: from,
                    feature_until: until,
                },
                { headers: authH },
            );
            const isLive = !!r.data?.is_featured;
            const scheduled = !!r.data?.scheduled && !isLive;
            setResults((rs) => rs.map((x) => (x.id === featureItem.id ? { ...x, is_featured: isLive } : x)));
            if (scheduled) {
                toast.success("Scheduled for homepage", { description: `Opens ${from ? new Date(from).toLocaleString() : "now"}` });
            } else if (isLive && until) {
                toast.success("Featured", { description: `Auto-drops ${new Date(until).toLocaleString()}` });
            } else {
                toast.success("Added to homepage");
            }
            setFeatureItem(null);
            onChanged?.();
        } catch (e) {
            toast.error("Feature failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setFeatureBusy(false);
        }
    };

    const openSms = async (row) => {
        setSmsItem(row);
        setSmsResult(null);
        setSmsBaseId("");
        try {
            const r = await axios.get(`${API}/customer-bases`, { headers: authH });
            setSmsBases(r.data || []);
        } catch { setSmsBases([]); }
    };

    const draftOrPushSms = async (push) => {
        if (!smsItem) return;
        setSmsBusy(true);
        try {
            const r = await axios.post(
                `${API}/admin/content-hub/sms-from-item`,
                {
                    kind: smsItem.kind,
                    item_id: smsItem.id,
                    customer_base_id: smsBaseId || null,
                    push_now: push,
                    // Pass-through context for ephemeral items (web_article,
                    // live ESPN fixture) so the backend has real text to feed
                    // the LLM instead of just a hash id.
                    title: smsItem.title,
                    summary: smsItem.summary,
                    url: smsItem.external_url || smsItem.url,
                },
                { headers: authH },
            );
            setSmsResult(r.data);
            if (push && r.data?.dispatched) {
                toast.success(`Pushed to ${r.data.dispatched.recipients} phones`);
            } else {
                toast.success("SMS draft created", { description: `${r.data.length} chars` });
            }
        } catch (e) {
            toast.error("SMS draft failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setSmsBusy(false);
        }
    };

    const resultCount = results.length;

    return (
        <div className="space-y-4" data-testid="content-hub-search">
            {/* Hero search bar */}
            <Card className="bg-gradient-to-br from-paper to-paper/60 border-electric/30 p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3 text-electric">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-[11px] uppercase tracking-[0.2em] font-heading font-bold">
                        Universal Curation Search
                    </span>
                </div>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                    <Input
                        autoFocus
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search team (Arsenal, Simba, Yanga), source (BBC, Goal), or any keyword…"
                        className="pl-11 pr-10 h-14 text-base bg-obsidian border-border focus:border-electric text-white placeholder:text-white/30"
                        data-testid="hub-search-input"
                    />
                    {q && (
                        <button
                            onClick={() => setQ("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                            data-testid="hub-search-clear"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {KIND_FILTERS.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setActiveKind(f.id)}
                            data-testid={`hub-search-filter-${f.id}`}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                activeKind === f.id
                                    ? "bg-electric text-white border-electric"
                                    : "bg-transparent text-white/60 border-border hover:border-electric/60 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <span className="ml-auto text-[11px] text-white/40">
                        {loading ? "Searching…" : debounced.length < 2 ? "Type at least 2 characters" : `${resultCount} results`}
                    </span>
                </div>
            </Card>

            {/* Results */}
            {loading && (
                <div className="flex items-center justify-center py-10 text-white/40">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Searching the catalogue…
                </div>
            )}

            {!loading && debounced.length >= 2 && resultCount === 0 && (
                <Card className="bg-paper border-border p-10 text-center" data-testid="hub-search-empty">
                    <div className="text-white/40 text-sm">
                        No results for <span className="text-white">"{debounced}"</span>. Try a different team or source.
                    </div>
                </Card>
            )}

            {!loading && resultCount > 0 && (
                <div className="space-y-2" data-testid="hub-search-results">
                    {results.map((r) => {
                        const meta = KIND_META[r.kind] || KIND_META.article;
                        const Icon = meta.Icon;
                        return (
                            <Card
                                key={`${r.kind}-${r.id}`}
                                className="bg-paper border-border p-4 hover:border-electric/40 transition-colors"
                                data-testid={`hub-result-${r.kind}-${r.id}`}
                            >
                                <div className="flex items-start gap-4">
                                    {r.image_url ? (
                                        <img
                                            src={r.image_url}
                                            alt=""
                                            className="w-20 h-20 rounded-md object-cover border border-border bg-obsidian flex-shrink-0"
                                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                                        />
                                    ) : (
                                        <div className={`w-20 h-20 rounded-md ${meta.tone} flex items-center justify-center flex-shrink-0`}>
                                            <Icon className="h-7 w-7 opacity-80" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                            <Badge className={`${meta.tone} border-0 text-[10px] uppercase tracking-wider font-bold`}>
                                                <Icon className="h-3 w-3 mr-1" />
                                                {meta.label}
                                            </Badge>

                                            {/* Live minute + pulsing dot */}
                                            {r.kind === "live_match" && (
                                                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-red-400 bg-red-400/10 border border-red-400/30 rounded-full px-2 py-0.5 uppercase tracking-wider" data-testid={`hub-live-badge-${r.id}`}>
                                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></span>
                                                    {r.minute_label || r.detail || "Live"}
                                                </span>
                                            )}

                                            {/* Final score for results */}
                                            {r.kind === "result" && r.score && (
                                                <Badge className="bg-orange-300/20 text-orange-300 border-orange-300/40 border text-[10px] font-mono tabular-nums" data-testid={`hub-score-${r.id}`}>
                                                    FT {r.score}
                                                </Badge>
                                            )}
                                            {r.kind === "live_match" && r.score && (
                                                <Badge className="bg-red-400/20 text-red-300 border-red-400/40 border text-[10px] font-mono tabular-nums">
                                                    {r.score}
                                                </Badge>
                                            )}
                                            {r.kind === "fixture" && r.kickoff && (
                                                <Badge variant="outline" className="text-[10px] border-cyan-300/40 text-cyan-300">
                                                    {new Date(r.kickoff).toLocaleString("sw-TZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                </Badge>
                                            )}

                                            {r.source && (
                                                <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                    {r.flag ? `${r.flag} ` : ""}{r.source}
                                                </Badge>
                                            )}
                                            {r.team && r.kind !== "match" && r.kind !== "prediction" && r.kind !== "live_match" && r.kind !== "fixture" && r.kind !== "result" && (
                                                <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                    {r.team}
                                                </Badge>
                                            )}
                                            {r.confidence !== undefined && r.confidence !== null && (
                                                <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-300">
                                                    {r.confidence}% conf
                                                </Badge>
                                            )}
                                            {r.is_featured && (
                                                <Badge className="bg-yellow-300/20 text-yellow-300 border-yellow-300/40 border text-[10px] uppercase tracking-wider">
                                                    <Star className="h-3 w-3 mr-1 fill-current" /> Featured
                                                </Badge>
                                            )}
                                        </div>
                                        <h3 className="font-heading font-bold text-white text-base leading-snug truncate">
                                            {r.title || "(untitled)"}
                                        </h3>
                                        {r.summary && (
                                            <p className="text-white/55 text-sm mt-1 line-clamp-2">{r.summary}</p>
                                        )}
                                        {r.external_url && (
                                            <a
                                                href={r.external_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] text-purple-300 hover:text-purple-200 inline-flex items-center gap-1 mt-1"
                                                data-testid={`hub-open-url-${r.id}`}
                                            >
                                                <Globe className="h-3 w-3" /> Open source →
                                            </a>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2 flex-shrink-0">
                                        <Button
                                            size="sm"
                                            variant={r.is_featured ? "secondary" : "default"}
                                            disabled={busyId === r.id}
                                            onClick={() => toggleFeature(r)}
                                            className={r.is_featured
                                                ? "bg-yellow-300/20 text-yellow-300 hover:bg-yellow-300/30 border border-yellow-300/40"
                                                : "bg-electric hover:bg-electric/90 text-white"}
                                            data-testid={`hub-feature-${r.kind}-${r.id}`}
                                        >
                                            {busyId === r.id ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : r.is_featured ? (
                                                <><StarOff className="h-3.5 w-3.5 mr-1.5" /> Unfeature</>
                                            ) : (
                                                <><Star className="h-3.5 w-3.5 mr-1.5" /> Add to Homepage</>
                                            )}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openSms(r)}
                                            className="border-border text-white hover:bg-electric hover:border-electric"
                                            data-testid={`hub-sms-${r.kind}-${r.id}`}
                                        >
                                            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Draft SMS
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Schedule feature dialog */}
            <Dialog open={!!featureItem} onOpenChange={(o) => !o && setFeatureItem(null)}>
                <DialogContent className="bg-paper border-border max-w-md" data-testid="hub-feature-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <CalendarClock className="h-5 w-5 text-electric" />
                            Feature on Homepage
                        </DialogTitle>
                        <DialogDescription className="text-xs text-white/50">
                            Pick a window — the item auto-features when the window opens and auto-drops when it closes.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm text-white/70 line-clamp-2">{featureItem?.title}</div>

                        {/* Quick presets */}
                        <div className="space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-white/50">Quick presets</div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    variant="outline"
                                    disabled={featureBusy}
                                    onClick={() => applyFeature("forever")}
                                    className="border-border text-white hover:bg-electric hover:border-electric h-auto py-2 flex-col gap-0.5"
                                    data-testid="hub-feature-forever"
                                >
                                    <span className="font-bold">Forever</span>
                                    <span className="text-[10px] text-white/50">Until manually removed</span>
                                </Button>
                                <Button
                                    variant="outline"
                                    disabled={featureBusy}
                                    onClick={() => applyFeature("24h")}
                                    className="border-border text-white hover:bg-electric hover:border-electric h-auto py-2 flex-col gap-0.5"
                                    data-testid="hub-feature-24h"
                                >
                                    <span className="font-bold">Next 24 h</span>
                                    <span className="text-[10px] text-white/50">Drops tomorrow</span>
                                </Button>
                                <Button
                                    variant="outline"
                                    disabled={featureBusy}
                                    onClick={() => applyFeature("48h")}
                                    className="border-border text-white hover:bg-electric hover:border-electric h-auto py-2 flex-col gap-0.5"
                                    data-testid="hub-feature-48h"
                                >
                                    <span className="font-bold">Next 48 h</span>
                                    <span className="text-[10px] text-white/50">2-day window</span>
                                </Button>
                                <Button
                                    variant="outline"
                                    disabled={featureBusy}
                                    onClick={() => applyFeature("week")}
                                    className="border-border text-white hover:bg-electric hover:border-electric h-auto py-2 flex-col gap-0.5"
                                    data-testid="hub-feature-week"
                                >
                                    <span className="font-bold">7 days</span>
                                    <span className="text-[10px] text-white/50">Auto-drops in a week</span>
                                </Button>
                            </div>
                        </div>

                        {/* Custom range */}
                        <div className="space-y-2 pt-2 border-t border-border">
                            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/50">
                                <Clock className="h-3 w-3" /> Custom window (optional)
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[10px] text-white/40 uppercase tracking-wider">From</label>
                                    <Input
                                        type="datetime-local"
                                        value={featureFrom}
                                        onChange={(e) => setFeatureFrom(e.target.value)}
                                        className="bg-obsidian border-border text-white text-sm"
                                        data-testid="hub-feature-from"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-white/40 uppercase tracking-wider">Until</label>
                                    <Input
                                        type="datetime-local"
                                        value={featureUntil}
                                        onChange={(e) => setFeatureUntil(e.target.value)}
                                        className="bg-obsidian border-border text-white text-sm"
                                        data-testid="hub-feature-until"
                                    />
                                </div>
                            </div>
                            <Button
                                disabled={featureBusy || (!featureFrom && !featureUntil)}
                                onClick={() => applyFeature("custom")}
                                className="w-full bg-electric hover:bg-electric/90 text-white mt-1"
                                data-testid="hub-feature-custom-apply"
                            >
                                {featureBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarClock className="h-4 w-4 mr-2" />}
                                Apply custom window
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* SMS draft dialog */}
            <Dialog open={!!smsItem} onOpenChange={(o) => !o && setSmsItem(null)}>
                <DialogContent className="bg-paper border-border max-w-lg" data-testid="hub-sms-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Send className="h-5 w-5 text-electric" />
                            SMS from "{smsItem?.title?.slice(0, 50)}…"
                        </DialogTitle>
                        <DialogDescription className="text-xs text-white/50">
                            Generate a 160-char Swahili SMS from this {smsItem?.kind || "item"} and save it as a campaign draft, or push it now to subscribers.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="text-xs text-white/60">
                            We'll AI-translate this to a 160-char Swahili SMS, save a draft campaign, and (optionally) push to subscribers right away.
                        </div>
                        <div className="space-y-2">
                            <label className="text-[11px] uppercase tracking-wider text-white/50">Customer Base (optional)</label>
                            <Select value={smsBaseId || "__all__"} onValueChange={(v) => setSmsBaseId(v === "__all__" ? "" : v)}>
                                <SelectTrigger className="bg-obsidian border-border text-white" data-testid="hub-sms-base">
                                    <SelectValue placeholder="All active subscribers" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">All active subscribers</SelectItem>
                                    {smsBases.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            {b.name} ({b.phone_count || b.count || 0})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {smsResult && (
                            <Card className="bg-obsidian border-electric/40 p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <Badge className="bg-electric/20 text-electric text-[10px] uppercase tracking-wider">
                                        {smsResult.dispatched ? "Dispatched" : "Drafted"}
                                    </Badge>
                                    <span className="text-[11px] text-white/40">{smsResult.length} chars</span>
                                </div>
                                <p className="text-sm text-white whitespace-pre-wrap" data-testid="hub-sms-preview">{smsResult.sms}</p>
                                {smsResult.dispatched && (
                                    <div className="text-[11px] text-white/50 mt-2">
                                        Sent to {smsResult.dispatched.recipients} phones · {smsResult.dispatched.delivered} delivered
                                    </div>
                                )}
                            </Card>
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            disabled={smsBusy}
                            onClick={() => draftOrPushSms(false)}
                            className="border-border text-white"
                            data-testid="hub-sms-draft"
                        >
                            {smsBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Save Draft
                        </Button>
                        {isAdmin && (
                            <Button
                                disabled={smsBusy}
                                onClick={() => draftOrPushSms(true)}
                                className="bg-electric hover:bg-electric/90 text-white"
                                data-testid="hub-sms-push"
                            >
                                {smsBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                                Push Now
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ContentHubSearch;
